import React, { useRef, useState, useCallback, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Eraser, Check, Undo2, Pen, Download } from 'lucide-react';

/**
 * Enhanced SignaturePad - Professional signature capture component
 * 
 * @param {function} onChange - Callback with base64 image data
 * @param {string} value - Current signature value (base64)
 * @param {boolean} disabled - Whether the pad is disabled
 * @param {string} size - 'default', 'compact', or 'expanded'
 * @param {string} className - Additional CSS classes
 * @param {boolean} showTools - Show pen color/thickness controls
 */
export const SignaturePad = ({ 
  onChange, 
  value = '', 
  disabled = false,
  size = 'default',
  className = '',
  showTools = true
}) => {
  const sigPadRef = useRef(null);
  const containerRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(!!value);
  const [penColor, setPenColor] = useState('#1e293b');
  const [penWidth, setPenWidth] = useState(2);
  const [history, setHistory] = useState([]);

  // Size configurations
  const heights = {
    compact: 100,
    default: 150,
    expanded: 220
  };
  const height = heights[size] || heights.default;

  // Color palette
  const colors = [
    { name: 'Black', value: '#1e293b' },
    { name: 'Blue', value: '#1d4ed8' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Green', value: '#16a34a' }
  ];

  // Initialize with existing value
  useEffect(() => {
    if (value && sigPadRef.current) {
      // Small delay to ensure canvas is ready
      setTimeout(() => {
        try {
          sigPadRef.current.fromDataURL(value, { 
            ratio: 1,
            width: containerRef.current?.offsetWidth || 400,
            height: height
          });
          setHasSignature(true);
        } catch (e) {
          console.error('Error loading signature:', e);
        }
      }, 100);
    }
  }, [value, height]);

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (sigPadRef.current && containerRef.current) {
        const canvas = sigPadRef.current.getCanvas();
        const dataUrl = hasSignature ? sigPadRef.current.toDataURL() : null;
        
        canvas.width = containerRef.current.offsetWidth;
        canvas.height = height;
        
        if (dataUrl && hasSignature) {
          sigPadRef.current.fromDataURL(dataUrl);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [height, hasSignature]);

  const saveToHistory = useCallback(() => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const dataUrl = sigPadRef.current.toDataURL();
      setHistory(prev => [...prev.slice(-9), dataUrl]); // Keep last 10 states
    }
  }, []);

  const handleEnd = useCallback(() => {
    if (sigPadRef.current && !disabled) {
      const dataUrl = sigPadRef.current.toDataURL('image/png');
      setHasSignature(!sigPadRef.current.isEmpty());
      onChange?.(dataUrl);
      saveToHistory();
    }
  }, [onChange, disabled, saveToHistory]);

  const handleBegin = useCallback(() => {
    if (disabled) {
      sigPadRef.current?.off();
    }
  }, [disabled]);

  const clearSignature = useCallback(() => {
    if (sigPadRef.current) {
      saveToHistory();
      sigPadRef.current.clear();
      setHasSignature(false);
      onChange?.('');
    }
  }, [onChange, saveToHistory]);

  const undoLast = useCallback(() => {
    if (history.length > 0) {
      const previousState = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      
      if (sigPadRef.current) {
        sigPadRef.current.clear();
        if (history.length > 1) {
          sigPadRef.current.fromDataURL(history[history.length - 2]);
          setHasSignature(true);
          onChange?.(history[history.length - 2]);
        } else {
          setHasSignature(false);
          onChange?.('');
        }
      }
    }
  }, [history, onChange]);

  const downloadSignature = useCallback(() => {
    if (sigPadRef.current && hasSignature) {
      const dataUrl = sigPadRef.current.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'signature.png';
      link.href = dataUrl;
      link.click();
    }
  }, [hasSignature]);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Tools Panel */}
      {showTools && !disabled && (
        <div className="flex items-center gap-4 p-2 bg-slate-50 rounded-lg border border-slate-200">
          {/* Pen Icon */}
          <div className="flex items-center gap-1 text-slate-600">
            <Pen className="w-4 h-4" />
            <span className="text-xs font-medium">Pen</span>
          </div>
          
          {/* Color Selection */}
          <div className="flex items-center gap-1">
            {colors.map(color => (
              <button
                key={color.value}
                type="button"
                onClick={() => setPenColor(color.value)}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  penColor === color.value 
                    ? 'border-indigo-500 scale-110 ring-2 ring-indigo-200' 
                    : 'border-transparent hover:border-slate-300'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              />
            ))}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-slate-300" />

          {/* Pen Thickness */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Size:</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4].map(width => (
                <button
                  key={width}
                  type="button"
                  onClick={() => setPenWidth(width)}
                  className={`w-7 h-7 rounded flex items-center justify-center transition-all ${
                    penWidth === width
                      ? 'bg-indigo-100 border-indigo-300 border'
                      : 'hover:bg-slate-100'
                  }`}
                  title={`${width}px`}
                >
                  <div 
                    className="rounded-full bg-slate-700"
                    style={{ 
                      width: `${width * 2 + 2}px`, 
                      height: `${width * 2 + 2}px` 
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Signature Canvas Container */}
      <div 
        ref={containerRef}
        className={`relative rounded-xl overflow-hidden transition-all ${
          disabled 
            ? 'bg-slate-100 border-2 border-slate-200' 
            : 'bg-white border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:shadow-sm'
        }`}
        style={{ height: `${height}px` }}
      >
        <SignatureCanvas
          ref={sigPadRef}
          penColor={penColor}
          minWidth={penWidth * 0.5}
          maxWidth={penWidth * 1.5}
          velocityFilterWeight={0.7}
          dotSize={penWidth}
          throttle={16}
          canvasProps={{
            className: `w-full h-full ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair'}`,
            style: { 
              width: '100%', 
              height: `${height}px`,
              touchAction: 'none'
            }
          }}
          onEnd={handleEnd}
          onBegin={handleBegin}
        />
        
        {/* Placeholder text */}
        {!hasSignature && !disabled && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 text-slate-400">
              <Pen className="w-5 h-5" />
              <span className="text-sm font-medium">Draw your signature here</span>
            </div>
            <span className="text-xs text-slate-300 mt-1">Use mouse or touch to sign</span>
          </div>
        )}

        {/* Signature baseline */}
        <div className="absolute bottom-5 left-6 right-6 border-b-2 border-slate-200 pointer-events-none" />
        
        {/* X mark for signature */}
        <span className="absolute bottom-6 left-4 text-slate-300 text-lg font-serif pointer-events-none">✗</span>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearSignature}
            disabled={disabled || !hasSignature}
            className="text-slate-500 hover:text-red-600 hover:bg-red-50"
            data-testid="signature-clear-btn"
          >
            <Eraser className="w-4 h-4 mr-1" />
            Clear
          </Button>
          
          {showTools && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={undoLast}
                disabled={disabled || history.length === 0}
                className="text-slate-500 hover:text-amber-600 hover:bg-amber-50"
                data-testid="signature-undo-btn"
              >
                <Undo2 className="w-4 h-4 mr-1" />
                Undo
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={downloadSignature}
                disabled={disabled || !hasSignature}
                className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                data-testid="signature-download-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Save
              </Button>
            </>
          )}
        </div>
        
        {hasSignature && (
          <span className="flex items-center text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
            <Check className="w-3 h-3 mr-1" />
            Signature captured
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Simplified Signature Pad for Public Forms
 * Minimal UI, no tools panel for cleaner form experience
 */
export const SignaturePadPublic = ({ onChange, value = '', size = 'default' }) => {
  return (
    <SignaturePad 
      onChange={onChange} 
      value={value} 
      size={size} 
      showTools={false}
    />
  );
};

export default SignaturePad;
